import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Container,
  Grid,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import styles from "./FAQ.module.css";
import { Helmet } from "react-helmet";

const FAQ = () => {
  const faqData = [
    {
      question: "What is AI Image Expand?",
      answer:
        "AI Image Expand is a cutting-edge tool that uses artificial intelligence to expand the boundaries of your images. It can intelligently generate new content to extend the edges of a photo, creating a wider or taller version of the original image.",
    },
    {
      question: "What types of images can I expand with this AI?",
      answer:
        "You can expand a wide range of images, including landscapes, portraits, product photos, and creative designs. The AI works best with images that have clear context and composition.",
    },
    {
      question: "How does the AI expand an image?",
      answer:
        "The AI analyzes the content of your image and generates new, contextually relevant pixels to extend the edges. It ensures that the expanded areas blend seamlessly with the original image.",
    },
    {
      question: "Can I control how the image is expanded?",
      answer:
        "Yes, you can guide the expansion process by specifying the direction (e.g., left, right, top, bottom) and the amount of expansion. You can also provide hints or prompts to influence the AI's output.",
    },
    {
      question: "How long does it take to expand an image?",
      answer:
        "Expansion typically takes between 10 to 30 seconds, depending on the image size, complexity, and the amount of expansion required.",
    },
    {
      question: "What file formats are supported for upload and download?",
      answer:
        "We support common image formats like JPEG, PNG, and TIFF for uploads. Expanded images can be downloaded in high-resolution formats, including JPEG and PNG.",
    },
    {
      question: "Can I use expanded images for commercial purposes?",
      answer:
        "Yes, all images expanded with our platform are royalty-free and can be used for personal or commercial projects, including marketing, printing, and digital media.",
    },
    {
      question: "Does the AI work on images with complex backgrounds?",
      answer:
        "Yes, the AI is designed to handle complex backgrounds and textures. It can intelligently generate new content that matches the style and context of the original image.",
    },
    {
      question: "Is my data safe when using AI Image Expand?",
      answer:
        "Absolutely. We prioritize user privacy and data security. Uploaded images are processed securely and are not stored on our servers after the expansion is complete.",
    },
    {
      question: "What resolutions are supported for expanded images?",
      answer:
        "We support resolutions from standard sizes (e.g., 512x512) up to 4K UHD (3840x2160) and beyond. Higher resolutions may require premium credits or subscriptions.",
    },
    {
      question: "Can I expand multiple images at once?",
      answer:
        "Yes, our platform supports batch processing, allowing you to upload and expand multiple images simultaneously for faster results.",
    },
  ];

  // Create schema markup for FAQ
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqData.map((faq) => ({
      "@type": "Question",
      name: faq.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.answer,
      },
    })),
  };

  return (
    <Container maxWidth="lg" className={styles.container}>
      <Helmet>
        {/* Structured Data */}
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <Typography variant="h2" className={styles.heading}>
        Frequently Asked Questions
      </Typography>
      <Typography variant="h6" className={styles.subheading}>
        Can't find the answer you're looking for? Reach out to our customer
        support team
      </Typography>

      <Grid item xs={12} md={8}>
        {faqData.map((faq, index) => (
          <Accordion key={index} className={styles.accordion}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon className={styles.expandIcon} />}
              aria-controls={`panel${index}-content`}
              id={`panel${index}-header`}
            >
              <Typography className={styles.question}>
                {faq.question}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography className={styles.answer}>{faq.answer}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </Grid>
    </Container>
  );
};

export default FAQ;
